import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CheckSquare, Clock, GripVertical, Loader2, Package, Plus, Square, Users, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Chip, Kpi, Loading, PageHeader, Panel } from "@/components/common/Chrome";
import { WorkTasks } from "@/components/common/WorkTasks";
import { WorkMoney } from "@/components/common/WorkMoney";
import { LogTimeDialog } from "@/components/common/LogTimeDialog";
import { DateField } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money, shortDate } from "@/lib/format";

const STAGE_ACCENT = {
  New: "border-t-muted-foreground", Planning: "border-t-primary", "In Progress": "border-t-violet-500",
  Assigned: "border-t-blue-500", Completed: "border-t-emerald-500",
  Returned: "border-t-amber-500", Cancelled: "border-t-red-500",
};

const PRIORITY_STYLE = {
  critical: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  high: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  medium: "border-primary/40 bg-primary/10 text-primary",
  low: "border-border bg-muted text-muted-foreground",
};

const TYPE_ICON = { event: CalendarClock, work_order: Package, project: Users, task: CheckSquare };

const BLANK = {
  title: "", work_type: "task", owning_domain: "program", domain_reference: "", owner: "",
  priority: "medium", due_date: "", description: "", event_detail: {},
};

export default function WorksDashboard() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [views, setViews] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [pick, setPick] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [loggingFor, setLoggingFor] = useState(null);

  const load = useCallback(async (programme) => {
    const { data } = await api.get(`/works${programme ? `?programme=${encodeURIComponent(programme)}` : ""}`);
    setData(data);
  }, []);

  const loadViews = useCallback(async () => {
    const [v, s] = await Promise.all([api.get("/works/views"), api.get("/works/subjects")]);
    setViews(v.data.views);
    setSubjects(s.data.subjects);
  }, []);

  useEffect(() => { loadViews().catch(() => setViews([])); }, [loadViews]);

  const switchView = async (code) => {
    setActiveView(code);
    await load(code);
  };

  const addView = async () => {
    const subject = subjects.find((s) => s.code === pick);
    if (!subject) return;
    setBusy("view");
    try {
      const { data } = await api.post("/works/views", {
        code: subject.code, name: subject.name, kind: subject.kind,
      });
      setViews(data.views);
      setPick("");
      toast.success(`${subject.name} added to your board`);
      await switchView(subject.code);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy("");
  };

  const removeView = async (code) => {
    const { data } = await api.delete(`/works/views/${encodeURIComponent(code)}`);
    setViews(data.views);
    if (activeView === code) await switchView("");
  };
  useEffect(() => { load().catch(() => setData({ items: [], stages: [], by_stage: {} })); }, [load]);
  const save = async () => {
    setBusy("save");
    try {
      const { data: created } = await api.post("/works", form);
      toast.success(`${created.work_id} created in stage New`);
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const moveTo = async (item, stage) => {
    if (!item || item.stage === stage) return;
    setBusy(item.id);
    try {
      await api.post(`/works/${item.id}/stage`, { stage, note: "Moved on the works board" });
      toast.success(`${item.work_id} → ${stage}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const toggleTask = async (item, index, done) => {
    setBusy(item.id);
    try {
      const { data: updated } = await api.post(`/works/${item.id}/tasks`, { index, done });
      setDetail(updated);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading works board" />;
  const items = data.items;
  const stages = data.stages;
  const overdue = items.filter((i) => i.due_date && new Date(i.due_date) < new Date()
    && !["Completed", "Cancelled"].includes(i.stage)).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-016 · Works / Project / Event Dashboard"
        title="One board for every work order, project and event"
        description="Work items from maintenance, programmes, events, finance and governance flow through a single stage pipeline. Drag a card to move it — every stage change is written to the audit log."
        actions={can("work", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-work-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> New work item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader>
                <DialogTitle>Create a work item</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Choosing type "event" seeds the task list, item list and budget template automatically.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Title" full>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                         data-testid="work-title-input" className="bg-muted/40" />
                </F>
                <F label="Work type">
                  <Select value={form.work_type} onValueChange={(v) => setForm({ ...form, work_type: v })}>
                    <SelectTrigger data-testid="work-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["task", "work_order", "project", "event"].map((t) => (
                        <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Owning domain">
                  <Select value={form.owning_domain} onValueChange={(v) => setForm({ ...form, owning_domain: v })}>
                    <SelectTrigger data-testid="work-domain-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["program", "property", "maintenance", "event", "finance", "governance", "volunteer"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="work-priority-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["low", "medium", "high", "critical"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Due date">
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                         data-testid="work-due-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Domain reference" full>
                  <Input value={form.domain_reference} onChange={(e) => setForm({ ...form, domain_reference: e.target.value })}
                         data-testid="work-reference-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Description" full>
                  <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            data-testid="work-description-input" className="bg-muted/40 text-xs" />
                </F>
              </div>
              <Button onClick={save} disabled={busy === "save" || !form.title} data-testid="work-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="works-view-tabs">
        <button type="button" onClick={() => switchView("")} data-testid="works-view-all"
                className={`rounded-full border px-3.5 py-1.5 text-[12px] transition-colors ${
                  activeView === ""
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-foreground hover:border-primary/60"}`}>
          All work
        </button>
        {views.map((v) => (
          <span key={v.code} className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
            activeView === v.code
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-foreground hover:border-primary/60"}`}>
            <button type="button" onClick={() => switchView(v.code)}
                    data-testid={`works-view-${v.code}`}>{v.name}</button>
            <button type="button" onClick={() => removeView(v.code)}
                    data-testid={`works-view-remove-${v.code}`}
                    className="opacity-60 hover:opacity-100">×</button>
          </span>
        ))}
        <div className="flex items-center gap-1.5">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="works-view-select"
                           className="h-8 w-[220px] bg-muted/40 text-[12px]">
              <SelectValue placeholder="Add a programme or event view" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {subjects.filter((s) => !views.some((v) => v.code === s.code)).map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name} · {s.kind} ({s.items})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={addView} disabled={!pick || busy === "view"}
                  data-testid="works-view-add" className="h-8 rounded-full border-border text-[11px]">
            {busy === "view" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add view"}
          </Button>
          <span className="text-[10.5px] text-muted-foreground">{views.length}/10 views</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-work-total" label="Work items" value={items.length} accent="sky" />
        <Kpi testId="kpi-work-progress" label="In progress" accent="violet"
             value={(data.by_stage["In Progress"] || 0) + (data.by_stage.Assigned || 0)} />
        <Kpi testId="kpi-work-returned" label="Returned / blocked" value={data.by_stage.Returned || 0} accent="amber" />
        <Kpi testId="kpi-work-overdue" label="Past due date" value={overdue} accent="red" />
      </div>

      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="works-board">
        {stages.map((stage) => {
          const column = items.filter((i) => i.stage === stage);
          return (
            <div
              key={stage}
              data-testid={`stage-column-${stage.replace(/\s/g, "-")}`}
              onDragOver={(e) => { e.preventDefault(); setOverStage(stage); }}
              onDragLeave={() => setOverStage((s) => (s === stage ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setOverStage(null);
                const item = items.find((i) => i.id === dragId);
                moveTo(item, stage);
                setDragId(null);
              }}
              className={`w-[260px] shrink-0 rounded-md border bg-card transition-colors duration-150 ${
                overStage === stage ? "border-primary/40 bg-primary/5" : "border-border"}`}
            >
              <div className={`flex items-center justify-between border-b border-border border-t-2 px-3 py-2.5 ${STAGE_ACCENT[stage]}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{stage}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{column.length}</span>
              </div>
              <div className="min-h-[120px] space-y-2 p-2">
                {column.length === 0 && (
                  <div className="rounded border border-dashed border-border px-2 py-6 text-center text-[10px] text-muted-foreground/70">
                    drop here
                  </div>
                )}
                {column.map((item) => {
                  const Icon = TYPE_ICON[item.work_type] || CheckSquare;
                  const late = item.due_date && new Date(item.due_date) < new Date()
                    && !["Completed", "Cancelled"].includes(item.stage);
                  return (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={() => setDragId(item.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => setDetail(item)}
                      data-testid={`work-card-${item.work_id}`}
                      className={`group cursor-grab rounded border border-border bg-muted/40 p-2.5 transition-colors duration-150 hover:border-primary/40 hover:bg-muted ${
                        dragId === item.id ? "opacity-40" : ""}`}
                    >
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/70 group-hover:text-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="font-mono text-[9px] text-muted-foreground/70">{item.work_id}</span>
                          </div>
                          <div className="mt-1 text-[11px] font-medium leading-snug text-foreground">{item.title}</div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1">
                            <Chip className={PRIORITY_STYLE[item.priority]}>{item.priority}</Chip>
                            <span className="font-mono text-[9px] text-muted-foreground/70">{item.owning_domain}</span>
                          </div>
                          {item.due_date && (
                            <div className={`mt-1 font-mono text-[9px] ${late ? "text-red-600 dark:text-red-400" : "text-muted-foreground/70"}`}>
                              due {shortDate(item.due_date)}
                            </div>
                          )}
                        </div>
                      </div>
                      {can("work", "write") && (
                        <div className="mt-2 hidden flex-wrap gap-1 group-hover:flex">
                          {stages.filter((s) => s !== item.stage).slice(0, 3).map((s) => (
                            <button key={s} onClick={(e) => { e.stopPropagation(); moveTo(item, s); }}
                                    data-testid={`move-${item.work_id}-to-${s.replace(/\s/g, "-")}`}
                                    className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground hover:bg-primary/20 hover:text-primary">
                              → {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {activeView && (
        <Panel testId="panel-view-tasks" kicker={`${views.find((v) => v.code === activeView)?.name || activeView}`}
               title="Every task on this programme or event"
               actions={(
                 <span className="font-mono text-[10.5px] text-muted-foreground"
                       data-testid="view-task-counters">
                   {data.task_counters?.done || 0}/{data.task_counters?.total || 0} done ·
                   {" "}{data.task_counters?.overdue || 0} overdue
                 </span>
               )}>
          {(data.tasks || []).length === 0 ? (
            <p className="px-3 py-6 text-center text-[11.5px] text-muted-foreground"
               data-testid="view-tasks-empty">
              No tasks on this programme or event yet — open a card above and add its checklist.
            </p>
          ) : (
          <div className="divide-y divide-border">
            {data.tasks.map((t) => (
              <div key={`${t.work_id}-${t.index}`} data-testid={`view-task-${t.work_id}-${t.index}`}
                   className="flex flex-wrap items-center gap-2 px-3 py-2">
                <button type="button" disabled={!can("work", "write")}
                        data-testid={`view-task-toggle-${t.work_id}-${t.index}`}
                        onClick={async () => {
                          try {
                            await api.put(`/works/${t.work_item_id}/tasks/${t.index}`,
                                          { done: !t.done });
                            await load(activeView);
                          } catch (e) { toast.error(apiError(e)); }
                        }}
                        className="shrink-0 text-muted-foreground hover:text-primary disabled:opacity-40">
                  {t.done ? <CheckSquare className="h-3.5 w-3.5 text-emerald-500" />
                    : <Square className="h-3.5 w-3.5" />}
                </button>
                <span className={`min-w-0 flex-1 text-[11.5px] ${t.done
                  ? "text-muted-foreground line-through" : "text-foreground"}`}>
                  {t.name}
                </span>
                <Chip className="border-border bg-muted text-muted-foreground">{t.work_id}</Chip>
                <span className="max-w-[220px] truncate font-mono text-[10px] text-muted-foreground/80">
                  {t.work_title}
                </span>
                <Chip className={t.status === "done"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : t.status === "overdue"
                    ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
                  {t.status}
                </Chip>
                {t.assignee && (
                  <span className="font-mono text-[10px] text-muted-foreground">{t.assignee}</span>
                )}
                {t.due_date && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    due {shortDate(t.due_date)}
                  </span>
                )}
              </div>
            ))}
          </div>
          )}
        </Panel>
      )}

      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-xl">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground" data-testid="work-detail-title">{detail.title}</SheetTitle>
              </SheetHeader>
              {can("work", "write") && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <Button size="sm" variant="outline" data-testid="work-edit-button"
                          onClick={() => { setEditForm({ title: detail.title, owner: detail.owner || "",
                            priority: detail.priority, due_date: detail.due_date || "",
                            description: detail.description || "" }); setEditing(true); }}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    Edit item
                  </Button>
                  <Button size="sm" variant="outline" data-testid="work-delete-button"
                          onClick={() => { setDeleteReason(""); setDeleting(true); }}
                          className="h-7 rounded-full border-border px-2.5 text-[10px] text-red-600 dark:text-red-400">
                    Delete item
                  </Button>
                </div>
              )}
              {editing && (
                <div className="mt-3 space-y-2 rounded-xl border border-border bg-muted/20 p-3"
                     data-testid="work-edit-form">
                  <Input value={editForm.title} data-testid="work-edit-title"
                         onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                         className="h-8 bg-muted/40 text-[11.5px]" />
                  <div className="flex flex-wrap items-center gap-2">
                    <Input value={editForm.owner} placeholder="Owner"
                           data-testid="work-edit-owner"
                           onChange={(e) => setEditForm({ ...editForm, owner: e.target.value })}
                           className="h-8 w-[200px] bg-muted/40 text-[11.5px]" />
                    <DateField value={editForm.due_date} testId="work-edit-due"
                               placeholder="Due date"
                               onChange={(v) => setEditForm({ ...editForm, due_date: v })}
                               className="h-8 w-[150px] text-[11px]" />
                    <Button size="sm" data-testid="work-edit-save"
                            onClick={async () => {
                              try {
                                const res = await api.put(`/works/${detail.id}`, editForm);
                                toast.success("Work item updated");
                                setDetail(res.data); setEditing(false); await load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}
                            className="h-8 rounded-full bg-primary px-3 text-[10px] text-primary-foreground hover:bg-primary/90">
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}
                            data-testid="work-edit-cancel"
                            className="h-8 rounded-full px-2 text-[10px] text-muted-foreground">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-1.5">
                <Chip className="border-border bg-muted text-foreground/80">{detail.work_id}</Chip>
                <Chip className="border-primary/40 bg-primary/10 text-primary">{detail.stage}</Chip>
                <Chip className={PRIORITY_STYLE[detail.priority]}>{detail.priority}</Chip>
                <Chip className="border-border bg-muted text-muted-foreground">{detail.work_type.replace(/_/g, " ")}</Chip>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{detail.description}</p>
              <div className="mt-3 space-y-1 rounded border border-border p-3 text-xs">
                <Line k="Owning domain" v={detail.owning_domain} />
                <Line k="Reference" v={detail.domain_reference || "—"} />
                <Line k="Owner" v={detail.owner} />
                <Line k="Due" v={detail.due_date ? shortDate(detail.due_date) : "—"} />
              </div>

              {can("work", "write") && (
                <div className="mt-4">
                  <div className="kicker mb-2">Move to stage</div>
                  <div className="flex flex-wrap gap-1.5">
                    {stages.map((s) => (
                      <Button key={s} size="sm" variant="outline" disabled={s === detail.stage || busy === detail.id}
                              onClick={async () => { await moveTo(detail, s); setDetail({ ...detail, stage: s }); }}
                              data-testid={`detail-move-${s.replace(/\s/g, "-")}`}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 space-y-4">
                <WorkTasks item={detail} canWrite={can("work", "write")}
                           onChanged={(updated) => { setDetail(updated); load(); }} />
                <WorkMoney item={detail} canWrite={can("work", "write")}
                           onChanged={(updated) => { setDetail(updated); load(); }} />
              </div>

              {can("volunteer", "write") && (
                <div className="mt-4">
                  <Button size="sm" variant="outline" data-testid="work-log-time-button"
                          onClick={() => setLoggingFor(detail)}
                          className="h-8 rounded-full border-border px-3 text-[10.5px]">
                    <Clock className="mr-1.5 h-3.5 w-3.5" /> Log time on this item
                  </Button>
                </div>
              )}

              {detail.work_type === "event" && detail.event_detail?.item_list && (
                <div className="mt-5 space-y-4">
                  <Panel testId="event-budget" kicker="Budget" title="Planned vs actual">
                    <div className="space-y-2 p-3">
                      <div className="flex items-center justify-between font-mono text-[11px]">
                        <span className="text-muted-foreground">Actual / planned</span>
                        <span className="text-foreground/80">
                          {money(detail.event_detail.budget?.actual, "INR", true)} / {money(detail.event_detail.budget?.planned, "INR", true)}
                        </span>
                      </div>
                      <Progress value={Math.min(((detail.event_detail.budget?.actual || 0)
                        / (detail.event_detail.budget?.planned || 1)) * 100, 100)} className="h-1.5 bg-muted" />
                      <div className="space-y-1 pt-1">
                        {(detail.event_detail.budget?.lines || []).map((l) => (
                          <div key={l.line} className="flex justify-between text-[11px]">
                            <span className="text-muted-foreground">{l.line}</span>
                            <span className="font-mono text-muted-foreground">{money(l.amount, "INR", true)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>

                  {(detail.event_detail.team || []).length > 0 && (
                    <Panel testId="event-team" kicker="Team" title="Assigned people">
                      <div className="divide-y divide-border">
                        {detail.event_detail.team.map((m) => (
                          <div key={m.name} className="flex items-center justify-between px-3 py-2 text-[11px]">
                            <span className="text-foreground/80">{m.name}</span>
                            <span className="font-mono text-[10px] text-muted-foreground">{m.role}</span>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}
                </div>
              )}

              {(detail.history || []).length > 0 && (
                <div className="mt-5">
                  <div className="kicker mb-2">Stage history</div>
                  <div className="space-y-1">
                    {detail.history.map((h, i) => (
                      <div key={i} className="rounded bg-muted/40 px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
                        {h.from} → <span className="text-foreground/80">{h.to}</span> · {h.by} · {dateTime(h.at)}
                        {h.note && (
                          <div className="mt-0.5 font-sans text-[10.5px] text-foreground/70"
                               data-testid={`work-history-note-${i}`}>{h.note}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={deleting} onOpenChange={(o) => !o && setDeleting(false)}>        <DialogContent className="max-w-md border-border bg-card" data-testid="work-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {detail?.work_id}?</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              The item leaves the board and is archived with your name and reason, so it can be
              restored if this was a mistake.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={deleteReason} data-testid="work-delete-reason"
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Why is this being deleted? (at least 5 characters)"
                    className="bg-muted/40 text-[12.5px]" />
          {deleteReason.trim().length > 0 && deleteReason.trim().length < 5 && (
            <p className="text-[11px] text-red-600 dark:text-red-400"
               data-testid="work-delete-reason-error">
              Give at least 5 characters so the archive says why.
            </p>
          )}
          <Button data-testid="work-delete-confirm"
                  disabled={busy === "del" || deleteReason.trim().length < 5}
                  onClick={async () => {
                    setBusy("del");
                    try {
                      const res = await api.delete(`/works/${detail.id}`,
                                                   { params: { reason: deleteReason.trim() } });
                      toast.success(res.data.message);
                      setDeleting(false);
                      setDetail(null);
                      await load(activeView);
                    } catch (e) { toast.error(apiError(e)); }
                    setBusy("");
                  }}
                  className="h-9 rounded-full bg-red-600 text-white hover:bg-red-600/90">
            {busy === "del" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Delete and archive
          </Button>
        </DialogContent>
      </Dialog>
      <LogTimeDialog open={Boolean(loggingFor)} onOpenChange={(o) => !o && setLoggingFor(null)}
                     title={`Log time · ${loggingFor?.title || ""}`}
                     fixed={loggingFor ? {
                       activity: loggingFor.work_type === "event" ? "event" : "task",
                       reference: loggingFor.work_id,
                     } : {}} />
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Line = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-right text-foreground/80">{String(v)}</span>
  </div>
);
